import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Award, Plus, Search, MoreHorizontal, Edit, Trash2, ChevronLeft, ChevronRight, Package, CheckCircle, X, DollarSign, TrendingUp, Activity, Download, Check, AlertCircle } from "lucide-react";
import { Transaction } from "../types";
import { DataService } from "../services/supabase";
import { CalculationService } from "../services/calculationService";
import { StatCard } from "../components/StatCard";
import { exportToSpreadsheet } from "../services/exportService";
import { formatDate as formatDateUtil } from "../lib/utils";
interface GradingProps {
  dataService?: DataService | null;
  onTransactionsUpdate?: (transactions: Transaction[]) => void;
  onGradingOrdersUpdate?: (gradingOrders: any[]) => void;
  gradingOrders?: any[];
  transactions?: Transaction[];
}

const Grading = ({ dataService, onTransactionsUpdate, onGradingOrdersUpdate, gradingOrders = [], transactions = [] }: GradingProps) => {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for sorting
  const [sortField] = useState<'submission_date' | 'order_id' | 'company'>('submission_date');
  const [sortDirection] = useState<'asc' | 'desc'>('desc');
  
  // State for accordion expansion
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [orderCards, setOrderCards] = useState<Record<string, Transaction[]>>({});
  
  // Pagination state for orders
  const [inProgressPage, setInProgressPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const itemsPerPage = 10;
  
  // Pagination state for cards within each order
  const [orderCardPages, setOrderCardPages] = useState<Record<string, number>>({});
  const cardsPerPage = 10;
  
  // State for exports
  const [isExporting, setIsExporting] = useState(false);
  
  // State for Add Order flow (multi-step)
  const [addOrderDialogOpen, setAddOrderDialogOpen] = useState(false);
  const [addOrderStep, setAddOrderStep] = useState<'details' | 'selectCards' | 'addNewCard'>('details');
  const [newOrderData, setNewOrderData] = useState({
    orderId: '',
    company: '',
    serviceLevel: '',
    submissionDate: '',
    cost: '',
    notes: '',
  });
  
  // State for card selection
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [cardSelectorSearch, setCardSelectorSearch] = useState('');
  
  // State for adding new card from card selector
  const [showNewCardDialog, setShowNewCardDialog] = useState(false);
  const [newCardData, setNewCardData] = useState({
    card: '',
    manufacturer: '',
    set: '',
    year: '',
    sport: '',
    purchase_date: '',
    cost: '',
    purchase_platform: '',
    bought_from: '',
    notes: '',
  });
  
  // State for Edit Order dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [editData, setEditData] = useState({
    orderId: '',
    company: '',
    serviceLevel: '',
    submissionDate: '',
    cost: '',
    notes: '',
  });

  // State for Change Cards dialog (from edit order)
  const [changeCardsDialogOpen, setChangeCardsDialogOpen] = useState(false);
  const [editingOrderCards, setEditingOrderCards] = useState<Transaction[]>([]);
  const [cardsToRemove, setCardsToRemove] = useState<Set<string>>(new Set());
  const [showAddCardsFromEdit, setShowAddCardsFromEdit] = useState(false);
  
  // State for Complete Order dialog
  const [completeOrderDialogOpen, setCompleteOrderDialogOpen] = useState(false);
  const [completingOrderId, setCompletingOrderId] = useState<string | null>(null);
  const [completeOrderData, setCompleteOrderData] = useState({
    returnDate: '',
    cardGrades: [] as {cardId: string, grade: string}[]
  });

  // State for Delete Order confirmation dialog
  const [deleteOrderDialogOpen, setDeleteOrderDialogOpen] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [deleteCardsWithOrder, setDeleteCardsWithOrder] = useState<boolean>(false);

  // Inline success/error message (replaces browser alerts)
  const [gradingMessage, setGradingMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const showGradingMessage = useCallback((type: 'success' | 'error', text: string) => {
    setGradingMessage({ type, text });
    setTimeout(() => setGradingMessage(null), 4000);
  }, []);

  const gradingOrdersByOrderId = useMemo(() => {
    const map = new Map<string, any>();
    gradingOrders.forEach(order => {
      if (order?.order_id) {
        map.set(order.order_id.trim(), order);
      }
    });
    return map;
  }, [gradingOrders]);

  const isTransactionInActiveGradingOrder = useCallback((transaction: Transaction) => {
    const gradingId = transaction.grading_order_id?.toString().trim();
    if (!gradingId) {
      return false;
    }

    const linkedOrder = gradingOrdersByOrderId.get(gradingId);
    // If the order doesn't exist, the card is not in an active grading order
    if (!linkedOrder) {
      return false;
    }

    const hasReturnDate =
      typeof linkedOrder.return_date === 'string'
        ? linkedOrder.return_date.trim() !== ''
        : Boolean(linkedOrder.return_date);

    return !hasReturnDate;
  }, [gradingOrdersByOrderId]);
  
  // Enrich and filter/sort grading orders
  const processedOrders = useMemo(() => {
    const enrichedOrders = CalculationService.enrichGradingOrders(gradingOrders, transactions);
    
    // Filter
    let filtered = enrichedOrders.filter(order => {
      const matchesOrderSearch = 
        order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.company && order.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.grading_service_level && order.grading_service_level.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Also check if any cards in this order match the search
      const orderCardsData = order.id ? (orderCards[order.id] || []) : [];
      const hasMatchingCard = searchTerm && orderCardsData.some(card => {
        return (
          (card.card && card.card.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.sport && card.sport.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.manufacturer && card.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.set && card.set.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
      
      return matchesOrderSearch || hasMatchingCard;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'submission_date':
          aValue = new Date(a.submission_date).getTime();
          bValue = new Date(b.submission_date).getTime();
          break;
        case 'order_id':
          aValue = a.order_id.toLowerCase();
          bValue = b.order_id.toLowerCase();
          break;
        case 'company':
          aValue = (a.company || '').toLowerCase();
          bValue = (b.company || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    return filtered;
  }, [gradingOrders, transactions, searchTerm, sortField, sortDirection, orderCards]);
  
  // Separate orders by status
  const inProgressOrders = processedOrders.filter(order => !order.return_date);
  const completedOrders = processedOrders.filter(order => order.return_date);

  // Helper function to flatten order data with cards for export
  const flattenOrderData = (orders: any[]): any[] => {
    const flattened: any[] = [];
    
    orders.forEach(order => {
      const orderCardsData = order.id ? (orderCards[order.id] || transactions.filter(t => t.grading_order_id === order.order_id)) : [];
      
      if (orderCardsData.length === 0) {
        // Order with no cards - still export order info
        flattened.push({
          'Order ID': order.order_id || '',
          'Company': order.company || '',
          'Service Level': order.grading_service_level || '',
          'Submission Date': order.submission_date || '',
          'Return Date': order.return_date || '',
          'Cost': typeof order.cost === 'number' ? order.cost : parseFloat(order.cost || '0'),
          'Number of Cards': order.number_of_cards || 0,
          'Notes': order.notes || '',
          'Card': '',
          'Sport/TCG': '',
          'Manufacturer': '',
          'Set': '',
          'Year': '',
          'Purchase Date': '',
          'Purchase Price': '',
          'Purchase Platform': '',
          'Grade': '',
        });
      } else {
        // Export each card as a separate row with order info
        orderCardsData.forEach(card => {
          flattened.push({
            'Order ID': order.order_id || '',
            'Company': order.company || '',
            'Service Level': order.grading_service_level || '',
            'Submission Date': order.submission_date || '',
            'Return Date': order.return_date || '',
            'Cost': typeof order.cost === 'number' ? order.cost : parseFloat(order.cost || '0'),
            'Number of Cards': order.number_of_cards || 0,
            'Notes': order.notes || '',
            'Card': card.card || '',
            'Sport/TCG': card.sport || '',
            'Manufacturer': card.manufacturer || '',
            'Set': card.set || '',
            'Year': card.year || '',
            'Purchase Date': card.purchase_date || '',
            'Purchase Price': typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0'),
            'Purchase Platform': card.purchase_platform || '',
            'Grade': card.grade || '',
          });
        });
      }
    });
    
    return flattened;
  };

  // Export in-progress orders
  const handleExportInProgress = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const exportData = flattenOrderData(inProgressOrders);
      
      if (exportData.length === 0) {
        showGradingMessage('error', 'No in-progress orders to export');
        setIsExporting(false);
        return;
      }

      exportToSpreadsheet(exportData, {
        filename: 'grading-orders-in-progress',
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting in-progress orders:', error);
      showGradingMessage('error', 'Failed to export in-progress orders. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export completed orders
  const handleExportCompleted = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const exportData = flattenOrderData(completedOrders);
      
      if (exportData.length === 0) {
        showGradingMessage('error', 'No completed orders to export');
        setIsExporting(false);
        return;
      }

      exportToSpreadsheet(exportData, {
        filename: 'grading-orders-completed',
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting completed orders:', error);
      showGradingMessage('error', 'Failed to export completed orders. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Calculate summary statistics for in-progress orders
  const gradingSummary = useMemo(() => {
    let totalValueAtGrading = 0;
    let totalItemsInGrading = 0;
    let totalGradingCost = 0;
    
    // Get all cards in in-progress grading orders
    const inProgressCards = transactions.filter(t => {
      return !t.sale_date && isTransactionInActiveGradingOrder(t);
    });
    
    // Calculate totals
    inProgressCards.forEach(card => {
      const purchaseCost = typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0');
      totalValueAtGrading += purchaseCost;
    });
    
    totalItemsInGrading = inProgressCards.length;
    
    // Calculate total grading cost by summing the cost of all in-progress grading orders
    inProgressOrders.forEach(order => {
      const orderCost = typeof order.cost === 'number' ? order.cost : parseFloat(order.cost || '0');
      totalGradingCost += orderCost;
    });
    
    return {
      totalValueAtGrading,
      totalItemsInGrading,
      totalGradingCost
    };
  }, [transactions, inProgressOrders, isTransactionInActiveGradingOrder]);
  
  // Paginated orders
  const paginatedInProgress = useMemo(() => {
    const startIndex = (inProgressPage - 1) * itemsPerPage;
    return inProgressOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [inProgressOrders, inProgressPage]);
  
  const paginatedCompleted = useMemo(() => {
    const startIndex = (completedPage - 1) * itemsPerPage;
    return completedOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [completedOrders, completedPage]);
  
  const totalInProgressPages = Math.ceil(inProgressOrders.length / itemsPerPage);
  const totalCompletedPages = Math.ceil(completedOrders.length / itemsPerPage);
  
  // Reset pagination when search or sorting changes
  useEffect(() => {
    setInProgressPage(1);
    setCompletedPage(1);
  }, [searchTerm, sortField, sortDirection]);
  
  // Helper functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };
  
  const getStatusText = (order: any) => {
    if (order.return_date) return 'Returned';
    const submissionDate = new Date(order.submission_date);
    const daysSince = Math.floor((Date.now() - submissionDate.getTime()) / (1000 * 60 * 60 * 24));
    return `${daysSince} days`;
  };
  
  const getStatusColor = (order: any) => {
    if (order.return_date) return 'default';
    const submissionDate = new Date(order.submission_date);
    const daysSince = Math.floor((Date.now() - submissionDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince > 60) return 'destructive';
    if (daysSince > 30) return 'warning';
    return 'default';
  };
  
  // Get cards for each grading order
  const getCardsForOrder = useCallback(async (orderId: string, orderIdString: string) => {
    try {
      const order = gradingOrders.find(o => o.id === orderId);
      if (!order) {
        console.error('Order not found:', orderId);
        return;
      }
      
      const result = await dataService?.getGradingOrderCards(orderIdString);
      if (result?.success && result.cards) {
        setOrderCards(prev => ({
          ...prev,
          [orderId]: result.cards
        }));
      }
    } catch (error) {
      console.error('Error fetching cards for order:', error);
    }
  }, [dataService, gradingOrders]);
  
  
  // Auto-expand orders with matching cards when search term changes (only if 3+ characters)
  useEffect(() => {
    // Only run this effect when searchTerm changes, not when data changes
    if (!searchTerm) {
      setExpandedOrders(new Set());
      return;
    }
    
    // Only auto-expand if search term is 3+ characters
    if (searchTerm.length < 3) {
      return;
    }
    
    // Find orders with matching cards and expand them
    const ordersToExpand = processedOrders.filter(order => {
      const orderCardsData = order.id ? (orderCards[order.id] || []) : [];
      return orderCardsData.some(card => {
        return (
          (card.card && card.card.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.sport && card.sport.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.manufacturer && card.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (card.set && card.set.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
    });
    
    // Expand all matching orders
    const newExpandedSet = new Set<string>();
    ordersToExpand.forEach(order => {
      if (order.id) {
        newExpandedSet.add(order.id);
        // Make sure cards are loaded for these orders
        if (!orderCards[order.id]) {
          getCardsForOrder(order.id, order.order_id);
        }
      }
    });
    
    setExpandedOrders(newExpandedSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);
  
  // Get available cards for selection (only cards not already in a grading order)
  const availableCards = useMemo(() => {
    return transactions.filter(tx => {
      const notSold = !tx.sale_date || tx.sale_date.trim() === '';
      if (!notSold) {
        return false;
      }

      const notGraded =
        !tx.grade ||
        (typeof tx.grade === 'string' && tx.grade.trim() === '');

      if (!notGraded) {
        return false;
      }

      return !isTransactionInActiveGradingOrder(tx);
    });
  }, [transactions, isTransactionInActiveGradingOrder]);
  
  // Filter available cards based on search
  const filteredAvailableCards = useMemo(() => {
    if (!cardSelectorSearch) return availableCards;
    
    const searchLower = cardSelectorSearch.toLowerCase();
    return availableCards.filter(card =>
      (card.card && card.card.toLowerCase().includes(searchLower)) ||
      (card.sport && card.sport.toLowerCase().includes(searchLower)) ||
      (card.manufacturer && card.manufacturer.toLowerCase().includes(searchLower)) ||
      (card.set && card.set.toLowerCase().includes(searchLower))
    );
  }, [availableCards, cardSelectorSearch]);
  
  // Handler for proceeding to card selection
  const handleProceedToCardSelection = () => {
    if (!newOrderData.orderId || !newOrderData.company || !newOrderData.serviceLevel || !newOrderData.submissionDate) {
      showGradingMessage('error', 'Please fill in all required fields (Order ID, Company, Service Level, Submission Date)');
      return;
    }
    setAddOrderStep('selectCards');
  };
  
  // Handler for adding a new card from the card selector
  const handleAddNewCardFromSelector = async () => {
    if (!dataService || !newCardData.card || !newCardData.purchase_date || !newCardData.cost) {
      showGradingMessage('error', 'Please fill in all required fields (Card, Purchase Date, Cost)');
      return;
    }
    
    try {
      const cardData: any = {
        card: newCardData.card,
        purchase_date: newCardData.purchase_date,
        cost: parseFloat(newCardData.cost),
      };
      
      if (newCardData.manufacturer) cardData.manufacturer = newCardData.manufacturer;
      if (newCardData.set) cardData.set = newCardData.set;
      if (newCardData.year) cardData.year = parseInt(newCardData.year);
      if (newCardData.sport) cardData.sport = newCardData.sport;
      if (newCardData.purchase_platform) cardData.purchase_platform = newCardData.purchase_platform;
      if (newCardData.bought_from) cardData.bought_from = newCardData.bought_from;
      if (newCardData.notes) cardData.notes = newCardData.notes;
      
      // If adding from change cards dialog, link to the editing order
      if (showAddCardsFromEdit && editingOrder) {
        cardData.grading_order_id = editingOrder.order_id;
      }
      
      await dataService.addTransaction(cardData);
      
      // Update local state instead of refreshing
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // If adding from change cards dialog, refresh order cards
      if (showAddCardsFromEdit && editingOrder) {
        if (editingOrder.order_id) {
          const result = await dataService.getGradingOrderCards(editingOrder.order_id);
          if (result?.success && result.cards) {
            setEditingOrderCards(result.cards);
            setOrderCards(prev => ({ ...prev, [editingOrder.id]: result.cards }));
          }
        }
      }
      
      // Close the add new card dialog
      setShowNewCardDialog(false);
      setNewCardData({
        card: '',
        manufacturer: '',
        set: '',
        year: '',
        sport: '',
        purchase_date: '',
        cost: '',
        purchase_platform: '',
        bought_from: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error adding new card:', error);
      showGradingMessage('error', 'Failed to add new card');
    }
  };
  
  // Handler for finalizing grading order creation
  const handleFinalizeOrder = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }
    
    if (selectedCardIds.size === 0) {
      showGradingMessage('error', 'Please select at least one card for this grading order');
      return;
    }
    
    try {
      // First, create the grading order
      await dataService.addGradingOrder({
        order_id: newOrderData.orderId,
        company: newOrderData.company,
        grading_service_level: newOrderData.serviceLevel,
        submission_date: newOrderData.submissionDate,
        cost: parseFloat(newOrderData.cost || '0'),
        notes: newOrderData.notes || null,
      });
      
      // Then, update all selected cards to link them to this grading order
      const cardIdsArray = Array.from(selectedCardIds);
      for (const cardId of cardIdsArray) {
        await dataService.updateTransaction(cardId, {
          grading_order_id: newOrderData.orderId
        });
      }
      
      // Update local state instead of refreshing
      if (onGradingOrdersUpdate) {
        const allGradingOrders = await dataService.getGradingOrders();
        onGradingOrdersUpdate(allGradingOrders);
      }
      
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Reset all state
      setAddOrderDialogOpen(false);
      setAddOrderStep('details');
      setSelectedCardIds(new Set());
      setCardSelectorSearch('');
      setNewOrderData({
        orderId: '', company: '', serviceLevel: '', submissionDate: '',
        cost: '', notes: '',
      });
    } catch (error) {
      console.error('Error creating grading order:', error);
      showGradingMessage('error', 'Failed to create grading order');
    }
  };
  
  // Handler for opening edit dialog
  const handleEditOrder = async (order: any) => {
    setEditingOrder(order);
    setEditData({
      orderId: order.order_id,
      company: order.company || '',
      serviceLevel: order.grading_service_level || '',
      submissionDate: order.submission_date,
      cost: String(order.cost || ''),
      notes: order.notes || '',
    });
    
    // Load cards for this order if not already loaded
    if (order.id && dataService) {
      const existingCards = orderCards[order.id];
      if (!existingCards && order.order_id) {
        await getCardsForOrder(order.id, order.order_id);
      }
    }
    
    setEditDialogOpen(true);
  };

  // Handler for opening Change Cards dialog
  const handleChangeCards = async () => {
    if (!editingOrder || !dataService) return;
    
    // Get cards for this order
    let cards = editingOrder.id ? (orderCards[editingOrder.id] || []) : [];
    
    // If cards aren't loaded, fetch them
    if (cards.length === 0 && editingOrder.order_id) {
      const result = await dataService.getGradingOrderCards(editingOrder.order_id);
      if (result?.success && result.cards) {
        cards = result.cards;
        setOrderCards(prev => ({ ...prev, [editingOrder.id]: result.cards }));
      }
    }
    
    setEditingOrderCards(cards);
    setCardsToRemove(new Set());
    setChangeCardsDialogOpen(true);
  };

  // Handler for removing cards from order
  const handleRemoveCardsFromOrder = async () => {
    if (!dataService || !editingOrder) return;
    
    try {
      // Remove grading_order_id from selected cards
      for (const cardId of Array.from(cardsToRemove)) {
        await dataService.updateTransaction(cardId, {
          grading_order_id: null
        });
      }
      
      // Update local state
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Update order cards state
      const remainingCards = editingOrderCards.filter(card => !cardsToRemove.has(card.id || ''));
      setEditingOrderCards(remainingCards);
      setOrderCards(prev => ({ ...prev, [editingOrder.id]: remainingCards }));
      
      // Clear selection
      setCardsToRemove(new Set());
    } catch (error) {
      console.error('Error removing cards from order:', error);
      showGradingMessage('error', 'Failed to remove cards from order');
    }
  };

  // Handler for adding cards to order (from change cards dialog)
  const handleAddCardsToOrder = async () => {
    if (!dataService || !editingOrder || selectedCardIds.size === 0) {
      return;
    }
    
    try {
      // Update all selected cards to link them to this grading order
      const cardIdsArray = Array.from(selectedCardIds);
      for (const cardId of cardIdsArray) {
        await dataService.updateTransaction(cardId, {
          grading_order_id: editingOrder.order_id
        });
      }
      
      // Update local state
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Refresh order cards
      if (editingOrder.order_id) {
        const result = await dataService.getGradingOrderCards(editingOrder.order_id);
        if (result?.success && result.cards) {
          setEditingOrderCards(result.cards);
          setOrderCards(prev => ({ ...prev, [editingOrder.id]: result.cards }));
        }
      }
      
      // Close add cards dialog and reset selection
      setShowAddCardsFromEdit(false);
      setSelectedCardIds(new Set());
      setCardSelectorSearch('');
    } catch (error) {
      console.error('Error adding cards to order:', error);
      showGradingMessage('error', 'Failed to add cards to order');
    }
  };
  
  // Handler for saving edits
  const handleSaveEdit = async () => {
    if (!dataService || !editingOrder || !editingOrder.id) {
      console.error('Data service or order not available');
      return;
    }
    
    if (!editData.orderId || !editData.company || !editData.serviceLevel || !editData.submissionDate) {
      showGradingMessage('error', 'Please fill in all required fields');
      return;
    }
    
    try {
      // If order_id changed, update all cards' grading_order_id
      if (editData.orderId !== editingOrder.order_id) {
        const orderCardsData = editingOrder.id ? (orderCards[editingOrder.id] || []) : [];
        for (const card of orderCardsData) {
          if (card.id) {
            await dataService.updateTransaction(card.id, {
              grading_order_id: editData.orderId
            });
          }
        }
      }
      
      await dataService.updateGradingOrder(editingOrder.id, {
        order_id: editData.orderId,
        company: editData.company,
        grading_service_level: editData.serviceLevel,
        submission_date: editData.submissionDate,
        cost: parseFloat(editData.cost || '0'),
        notes: editData.notes || null,
      });
      
      // Update local state instead of refreshing
      if (onGradingOrdersUpdate) {
        const updatedGradingOrders = gradingOrders.map(o => 
          o.id === editingOrder.id 
            ? { 
                ...o, 
                order_id: editData.orderId,
                company: editData.company,
                grading_service_level: editData.serviceLevel,
                submission_date: editData.submissionDate,
                cost: parseFloat(editData.cost || '0'),
                notes: editData.notes || null,
                updated_at: new Date().toISOString()
              }
            : o
        );
        onGradingOrdersUpdate(updatedGradingOrders);
      }
      
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      setEditDialogOpen(false);
      setEditingOrder(null);
    } catch (error) {
      console.error('Error updating grading order:', error);
      showGradingMessage('error', 'Failed to update grading order');
    }
  };
  
  // Handler for opening delete confirmation dialog
  const handleDeleteOrderClick = (id: string) => {
    setDeletingOrderId(id);
    setDeleteOrderDialogOpen(true);
    setDeleteCardsWithOrder(false); // Default to sending back to inventory
  };

  // Handler for deleting an order
  const handleDeleteOrder = async () => {
    if (!dataService || !deletingOrderId) {
      console.error('Data service or order ID not available');
      return;
    }

    try {
      const order = gradingOrders.find(o => o.id === deletingOrderId);
      if (!order) {
        showGradingMessage('error', 'Order not found');
        return;
      }

      // Get cards associated with this order - fetch if not already loaded
      let orderCardsData = order.id ? (orderCards[order.id] || []) : [];
      
      // If cards aren't loaded, fetch them
      if (orderCardsData.length === 0 && order.order_id) {
        const result = await dataService.getGradingOrderCards(order.order_id);
        if (result?.success && result.cards) {
          orderCardsData = result.cards;
          // Also update state for UI consistency
          setOrderCards(prev => ({ ...prev, [deletingOrderId]: result.cards }));
        }
      }
      
      if (deleteCardsWithOrder) {
        // Delete cards along with the order
        // First delete all associated cards
        for (const card of orderCardsData) {
          if (card.id) {
            await dataService.deleteTransaction(card.id);
          }
        }
      } else {
        // Send cards back to inventory (remove grading_order_id)
        for (const card of orderCardsData) {
          if (card.id) {
            await dataService.updateTransaction(card.id, {
              grading_order_id: null
            });
          }
        }
      }

      // Now delete the grading order
      await dataService.deleteGradingOrder(deletingOrderId);
      
      // Update local state instead of refreshing
      if (onGradingOrdersUpdate) {
        const updatedGradingOrders = gradingOrders.filter(o => o.id !== deletingOrderId);
        onGradingOrdersUpdate(updatedGradingOrders);
      }

      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }

      // Close dialog and reset state
      setDeleteOrderDialogOpen(false);
      setDeletingOrderId(null);
      setDeleteCardsWithOrder(false);
    } catch (error) {
      console.error('Error deleting grading order:', error);
      showGradingMessage('error', 'Failed to delete grading order');
    }
  };
  
  // Handler for completing an order
  const handleCompleteOrder = async () => {
    if (!dataService || !completingOrderId) {
      console.error('Data service or order not available');
      return;
    }
    
    if (!completeOrderData.returnDate) {
      showGradingMessage('error', 'Please provide a return date');
      return;
    }
    
    try {
      // Get the grading order to find the company
      const order = gradingOrders.find(o => o.id === completingOrderId);
      const gradingCompany = order?.company || '';
      
      // Update the grading order with return date
      await dataService.updateGradingOrder(completingOrderId, {
        return_date: completeOrderData.returnDate
      });
      
      // Update each card with its grade (prepend company to grade number)
      for (const cardGrade of completeOrderData.cardGrades) {
        if (cardGrade.grade.trim()) {
          const fullGrade = gradingCompany ? `${gradingCompany} ${cardGrade.grade}` : cardGrade.grade;
          await dataService.updateTransaction(cardGrade.cardId, {
            grade: fullGrade
          });
        }
      }
      
      // Update local state instead of refreshing
      if (onGradingOrdersUpdate) {
        const updatedGradingOrders = gradingOrders.map(o => 
          o.id === completingOrderId 
            ? { ...o, return_date: completeOrderData.returnDate, updated_at: new Date().toISOString() }
            : o
        );
        onGradingOrdersUpdate(updatedGradingOrders);
      }
      
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      setCompleteOrderDialogOpen(false);
      setCompletingOrderId(null);
      setCompleteOrderData({ returnDate: '', cardGrades: [] });
    } catch (error) {
      console.error('Error completing order:', error);
      showGradingMessage('error', 'Failed to complete order');
    }
  };
  
  // Load cards when complete order dialog opens
  useEffect(() => {
    if (completeOrderDialogOpen && completingOrderId) {
      const order = gradingOrders.find(o => o.id === completingOrderId);
      if (order && dataService) {
        // Check if we already have the cards loaded
        const existingCards = orderCards[completingOrderId];
        if (existingCards && existingCards.length > 0) {
          // Cards already loaded, just initialize the grades array
          setCompleteOrderData(prev => ({
            ...prev,
            cardGrades: existingCards.map((card: Transaction) => ({ cardId: card.id || '', grade: '' }))
          }));
        } else if (!existingCards) {
          // Need to fetch cards
          dataService.getGradingOrderCards(order.order_id)
            .then((result: any) => {
              if (result?.success && result.cards) {
                setOrderCards(prev => ({ ...prev, [completingOrderId!]: result.cards }));
                setCompleteOrderData(prev => ({
                  ...prev,
                  cardGrades: result.cards.map((card: Transaction) => ({ cardId: card.id || '', grade: '' }))
                }));
              }
            })
            .catch(error => {
              console.error('Error loading order cards for completion:', error);
            });
        }
      }
    }
  }, [completeOrderDialogOpen, completingOrderId, orderCards, gradingOrders, dataService]);

  // Load cards when delete order dialog opens
  useEffect(() => {
    if (deleteOrderDialogOpen && deletingOrderId) {
      const order = gradingOrders.find(o => o.id === deletingOrderId);
      if (order && dataService) {
        // Check if we already have the cards loaded
        const existingCards = orderCards[deletingOrderId];
        if (!existingCards) {
          // Need to fetch cards
          getCardsForOrder(deletingOrderId, order.order_id);
        }
      }
    }
  }, [deleteOrderDialogOpen, deletingOrderId, orderCards, gradingOrders, dataService, getCardsForOrder]);
  
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!addOrderDialogOpen) {
      setAddOrderStep('details');
      setSelectedCardIds(new Set());
      setCardSelectorSearch('');
      setNewOrderData({
        orderId: '', company: '', serviceLevel: '', submissionDate: '',
        cost: '', notes: '',
      });
    }
  }, [addOrderDialogOpen]);
  
  // Render a single order card
  const renderOrderCard = (order: any) => {
    if (!order.id) return null; // Skip orders without IDs
    
    const isExpanded = expandedOrders.has(order.id);
    const allCards = orderCards[order.id] || [];
    
    // Filter cards based on search term if it exists
    const cards = searchTerm && searchTerm.length >= 1
      ? allCards.filter(card =>
        (card.card && card.card.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (card.sport && card.sport.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (card.manufacturer && card.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (card.set && card.set.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      : allCards;
    
    const currentCardPage = orderCardPages[order.id] || 0;
    const totalCardPages = Math.ceil(cards.length / cardsPerPage);
    const startIndex = currentCardPage * cardsPerPage;
    const paginatedCards = cards.slice(startIndex, startIndex + cardsPerPage);
    
    return (
      <Card key={order.id} className="mb-4 animate-fade-in">
        <Accordion 
          key={`accordion-${order.id}-${isExpanded}`}
          type="single" 
          collapsible 
          value={isExpanded ? order.id : undefined}
          onValueChange={(value) => {
            // Immediately update our state to match what Accordion wants
            if (value === order.id) {
              // Opening
              setExpandedOrders(prev => {
                const newSet = new Set(prev);
                newSet.add(order.id);
                return newSet;
              });
              // Load cards if not already loaded
              if (!orderCards[order.id]) {
                getCardsForOrder(order.id, order.order_id);
              }
              setOrderCardPages(prevPages => ({ ...prevPages, [order.id]: 0 }));
            } else {
              // Closing - ensure we remove it immediately
              setExpandedOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(order.id);
                return newSet;
              });
            }
          }}
        >
          <AccordionItem value={order.id} className="border-none">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2 sm:mb-0">
                  <h3 className="font-semibold text-lg">{order.order_id}</h3>
                  <Badge variant="outline" className="bg-primary/10 border-primary">
                    {order.company}
                  </Badge>
                  <Badge variant="outline" className="bg-secondary/10 border-secondary">
                    {order.grading_service_level}
                  </Badge>
                  <Badge variant="outline" className={`bg-${getStatusColor(order)}/10 border-${getStatusColor(order)}`}>
                    {getStatusText(order)}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Submitted: {formatDateUtil(order.submission_date, { fallback: 'N/A' })}
                  </span>
                  {order.return_date && (
                    <span className="text-sm text-muted-foreground">
                      Returned: {formatDateUtil(order.return_date, { fallback: 'N/A' })}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOrder(order);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          handleEditOrder(order);
                        }}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit Order
                      </DropdownMenuItem>
                      {!order.return_date && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setCompletingOrderId(order.id);
                              setCompleteOrderDialogOpen(true);
                            }}
                            className="flex items-center gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Complete Order
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          handleDeleteOrderClick(order.id);
                        }}
                        className="flex items-center gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                {/* Order Details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Cost</div>
                    <div className="font-medium">{formatCurrency((order as any).cost || 0)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Cards</div>
                    <div className="font-medium">{order.number_of_cards || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Cost per Card</div>
                    <div className="font-medium">{formatCurrency((order as any).cost_per_card || 0)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Gems</div>
                    <div className="font-medium">{(order as any).total_gems || '-'}</div>
                  </div>
                </div>
                
                {/* Cards in this order */}
                <div>
                  <h4 className="font-semibold mb-2">Cards in this Order ({cards.length})</h4>
                  {cards.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        {paginatedCards.map((card, index) => (
                          <div key={card.id || index} className="flex items-start gap-3 p-3 glass-card rounded-lg">
                            <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
                            <div className="flex-1">
                              <div className="font-medium">{card.card}</div>
                              <div className="text-sm text-muted-foreground">
                                {card.sport} • {formatCurrency(typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0'))} • {formatDateUtil(card.purchase_date)}
                                {card.grade && (
                                  <span className="ml-2">Grade: {card.grade}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {totalCardPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOrderCardPages(prev => ({ ...prev, [order.id]: Math.max(0, currentCardPage - 1) }))}
                            disabled={currentCardPage === 0}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            Page {currentCardPage + 1} of {totalCardPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOrderCardPages(prev => ({ ...prev, [order.id]: Math.min(totalCardPages - 1, currentCardPage + 1) }))}
                            disabled={currentCardPage === totalCardPages - 1}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {searchTerm.length >= 1 ?
                        `No cards match "${searchTerm}" in this order` :
                        'No cards found for this order'
                      }
                    </p>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    );
  };
  
  const renderPaginationControls = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="text-sm text-muted-foreground">
        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, processedOrders.length)} of {processedOrders.length} items
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <div className="text-sm text-muted-foreground">
          Page {currentPage} of {totalPages}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-background">
      <main className="container py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Grading Orders</h1>
            <p className="text-muted-foreground mt-1">
              Manage your card grading submissions
            </p>
          </div>
          <Button variant="gradient" size="sm" className="gap-2" onClick={() => setAddOrderDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Grading Order</span>
            <span className="sm:hidden">New Order</span>
          </Button>
        </div>

        {/* Inline success/error message */}
        {gradingMessage && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            gradingMessage.type === 'success'
              ? 'bg-success/10 border-success/20'
              : 'bg-destructive/10 border-destructive/20'
          }`}>
            {gradingMessage.type === 'success' ? (
              <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-success" />
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            )}
            <p className={`flex-1 text-sm ${gradingMessage.type === 'success' ? 'text-success' : 'text-destructive'}`}>
              {gradingMessage.text}
            </p>
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => setGradingMessage(null)} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Grading Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Total Value at Grading"
            value={`$${gradingSummary.totalValueAtGrading.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            change={`${gradingSummary.totalItemsInGrading} items`}
            changeType="positive"
            icon={DollarSign}
            gradient="secondary"
          />
          <StatCard
            title="Items in Grading"
            value={gradingSummary.totalItemsInGrading.toString()}
            change={`${inProgressOrders.length} orders`}
            changeType="neutral"
            icon={Activity}
            gradient="primary"
          />
          <StatCard
            title="Total Grading Cost"
            value={`$${gradingSummary.totalGradingCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            change="Open orders"
            changeType="neutral"
            icon={TrendingUp}
            gradient="tertiary"
          />
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search grading orders or cards..."
            className="pl-10 glass-card"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* In Progress Orders */}
        <Card className="glass-card">
          <CardContent>
            <div className="py-4 border-b mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Award className="h-5 w-5 text-warning" />
                In Progress Orders ({inProgressOrders.length})
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportInProgress}
                disabled={isExporting || inProgressOrders.length === 0}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download Spreadsheet</span>
                <span className="sm:hidden">Export</span>
              </Button>
            </div>
            {paginatedInProgress.length > 0 ? (
              <div>
                {paginatedInProgress.map(renderOrderCard)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No in progress orders found.
              </p>
            )}
            {totalInProgressPages > 1 && renderPaginationControls(inProgressPage, totalInProgressPages, setInProgressPage)}
          </CardContent>
        </Card>
        
        {/* Completed Orders */}
        <Card className="glass-card">
          <CardContent>
            <div className="py-4 border-b mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Award className="h-5 w-5 text-success" />
                Completed Orders ({completedOrders.length})
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCompleted}
                disabled={isExporting || completedOrders.length === 0}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download Spreadsheet</span>
                <span className="sm:hidden">Export</span>
              </Button>
            </div>
            {paginatedCompleted.length > 0 ? (
              <div>
                {paginatedCompleted.map(renderOrderCard)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No completed orders found.
              </p>
            )}
            {totalCompletedPages > 1 && renderPaginationControls(completedPage, totalCompletedPages, setCompletedPage)}
          </CardContent>
        </Card>
      </main>
      
      {/* Add New Order Dialog - Multi-Step */}
      <Dialog open={addOrderDialogOpen} onOpenChange={setAddOrderDialogOpen}>
        {addOrderStep === 'details' && (
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Grading Order</DialogTitle>
              <DialogDescription>
                Enter details for your grading submission. Fields marked with * are required.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order-id">Order ID *</Label>
                  <Input
                    id="order-id"
                    value={newOrderData.orderId}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, orderId: e.target.value }))}
                    placeholder="e.g., GR004"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company *</Label>
                  <Input
                    id="company"
                    value={newOrderData.company}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="e.g., PSA, BGS, SGC"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service-level">Service Level *</Label>
                  <Input
                    id="service-level"
                    value={newOrderData.serviceLevel}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, serviceLevel: e.target.value }))}
                    placeholder="e.g., Economy, Regular, Express"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="submission-date">Submission Date *</Label>
                  <Input
                    id="submission-date"
                    type="date"
                    value={newOrderData.submissionDate}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, submissionDate: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost">Total Cost</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  value={newOrderData.cost}
                  onChange={(e) => setNewOrderData(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={newOrderData.notes}
                  onChange={(e) => setNewOrderData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOrderDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="gradient" onClick={handleProceedToCardSelection}>
                Add Cards
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
        
        {addOrderStep === 'selectCards' && (
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Select Cards for {newOrderData.orderId}</DialogTitle>
              <DialogDescription>
                Choose cards from your inventory to include in this grading order
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search cards..."
                  className="pl-10"
                  value={cardSelectorSearch}
                  onChange={(e) => setCardSelectorSearch(e.target.value)}
                />
              </div>
              
              {/* Add new card button */}
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={() => setShowNewCardDialog(true)}
              >
                <Plus className="h-4 w-4" />
                Add New Card to This Order
              </Button>
              
              {/* Selected count */}
              {selectedCardIds.size > 0 && (
                <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary">
                  <span className="font-medium">
                    {selectedCardIds.size} card{selectedCardIds.size !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCardIds(new Set())}
                    className="gap-2"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                </div>
              )}
              
              {/* Card list */}
              {filteredAvailableCards.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredAvailableCards.map((card) => (
                    <div
                      key={card.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedCardIds.has(card.id || '')
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedCardIds);
                        if (newSet.has(card.id || '')) {
                          newSet.delete(card.id || '');
                        } else {
                          newSet.add(card.id || '');
                        }
                        setSelectedCardIds(newSet);
                      }}
                    >
                      <Checkbox
                        checked={selectedCardIds.has(card.id || '')}
                        onCheckedChange={() => {
                          const newSet = new Set(selectedCardIds);
                          if (newSet.has(card.id || '')) {
                            newSet.delete(card.id || '');
                          } else {
                            newSet.add(card.id || '');
                          }
                          setSelectedCardIds(newSet);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium">{card.card}</div>
                        <div className="text-sm text-muted-foreground">
                          {card.sport} • {formatCurrency(typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0'))} • {formatDateUtil(card.purchase_date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {cardSelectorSearch ? 'No cards match your search' : 'No available cards in inventory'}
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOrderStep('details')}>
                Back
              </Button>
              <Button 
                variant="gradient" 
                onClick={handleFinalizeOrder}
                disabled={selectedCardIds.size === 0}
              >
                Add Order ({selectedCardIds.size} {selectedCardIds.size === 1 ? 'card' : 'cards'})
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
      
      {/* Add New Card Dialog (from card selector) */}
      <Dialog open={showNewCardDialog} onOpenChange={setShowNewCardDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Card</DialogTitle>
            <DialogDescription>
              Add a new card that will be included in this grading order
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-card-name">Card Name *</Label>
                <Input
                  id="new-card-name"
                  value={newCardData.card}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, card: e.target.value }))}
                  placeholder="e.g., 2021 Topps Now LeBron James"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-card-sport">Sport</Label>
                <Input
                  id="new-card-sport"
                  value={newCardData.sport}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, sport: e.target.value }))}
                  placeholder="e.g., NBA, MLB, NFL"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-card-manufacturer">Manufacturer</Label>
                <Input
                  id="new-card-manufacturer"
                  value={newCardData.manufacturer}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  placeholder="e.g., Topps, Panini"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-card-set">Set</Label>
                <Input
                  id="new-card-set"
                  value={newCardData.set}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, set: e.target.value }))}
                  placeholder="e.g., Topps Now, Bowman"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-card-year">Year</Label>
                <Input
                  id="new-card-year"
                  type="number"
                  value={newCardData.year}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="2024"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-card-purchase-date">Purchase Date *</Label>
                <Input
                  id="new-card-purchase-date"
                  type="date"
                  value={newCardData.purchase_date}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, purchase_date: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-card-cost">Cost *</Label>
                <Input
                  id="new-card-cost"
                  type="number"
                  step="0.01"
                  value={newCardData.cost}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-card-platform">Purchase Platform</Label>
                <Input
                  id="new-card-platform"
                  value={newCardData.purchase_platform}
                  onChange={(e) => setNewCardData(prev => ({ ...prev, purchase_platform: e.target.value }))}
                  placeholder="e.g., eBay, COMC"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-card-bought-from">Bought From</Label>
              <Input
                id="new-card-bought-from"
                value={newCardData.bought_from}
                onChange={(e) => setNewCardData(prev => ({ ...prev, bought_from: e.target.value }))}
                placeholder="e.g., Seller name, Store name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-card-notes">Notes</Label>
              <Input
                id="new-card-notes"
                value={newCardData.notes}
                onChange={(e) => setNewCardData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCardDialog(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleAddNewCardFromSelector}>
              Add Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Grading Order</DialogTitle>
            <DialogDescription>
              Update grading order details. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-order-id">Order ID *</Label>
                <Input
                  id="edit-order-id"
                  value={editData.orderId}
                  onChange={(e) => setEditData(prev => ({ ...prev, orderId: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-company">Company *</Label>
                <Input
                  id="edit-company"
                  value={editData.company}
                  onChange={(e) => setEditData(prev => ({ ...prev, company: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-service-level">Service Level *</Label>
                <Input
                  id="edit-service-level"
                  value={editData.serviceLevel}
                  onChange={(e) => setEditData(prev => ({ ...prev, serviceLevel: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-submission-date">Submission Date *</Label>
                <Input
                  id="edit-submission-date"
                  type="date"
                  value={editData.submissionDate}
                  onChange={(e) => setEditData(prev => ({ ...prev, submissionDate: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cost">Total Cost</Label>
              <Input
                id="edit-cost"
                type="number"
                step="0.01"
                value={editData.cost}
                onChange={(e) => setEditData(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
            <div className="pt-2">
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={handleChangeCards}
              >
                <Package className="h-4 w-4" />
                Change Cards
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Cards Dialog (from Edit Order) */}
      <Dialog open={changeCardsDialogOpen} onOpenChange={setChangeCardsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Cards for {editingOrder?.order_id}</DialogTitle>
            <DialogDescription>
              Manage cards in this grading order. Select cards to remove, or add new cards.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Cards Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Current Cards in Order ({editingOrderCards.length})</h4>
                {cardsToRemove.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveCardsFromOrder}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove Selected ({cardsToRemove.size})
                  </Button>
                )}
              </div>
              
              {editingOrderCards.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {editingOrderCards.map((card) => (
                    <div
                      key={card.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        cardsToRemove.has(card.id || '')
                          ? 'border-destructive bg-destructive/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        const newSet = new Set(cardsToRemove);
                        if (newSet.has(card.id || '')) {
                          newSet.delete(card.id || '');
                        } else {
                          newSet.add(card.id || '');
                        }
                        setCardsToRemove(newSet);
                      }}
                    >
                      <Checkbox
                        checked={cardsToRemove.has(card.id || '')}
                        onCheckedChange={() => {
                          const newSet = new Set(cardsToRemove);
                          if (newSet.has(card.id || '')) {
                            newSet.delete(card.id || '');
                          } else {
                            newSet.add(card.id || '');
                          }
                          setCardsToRemove(newSet);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium">{card.card}</div>
                        <div className="text-sm text-muted-foreground">
                          {card.sport} • {formatCurrency(typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0'))} • {formatDateUtil(card.purchase_date)}
                          {card.grade && (
                            <span className="ml-2">Grade: {card.grade}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No cards in this order</p>
                </div>
              )}
            </div>
            
            {/* Add Cards Button */}
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={() => setShowAddCardsFromEdit(true)}
            >
              <Plus className="h-4 w-4" />
              Add Cards to Order
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setChangeCardsDialogOpen(false);
              setCardsToRemove(new Set());
            }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Cards Dialog (from Change Cards) */}
      <Dialog open={showAddCardsFromEdit} onOpenChange={setShowAddCardsFromEdit}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Cards to {editingOrder?.order_id}</DialogTitle>
            <DialogDescription>
              Choose cards from your inventory to add to this grading order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search cards..."
                className="pl-10"
                value={cardSelectorSearch}
                onChange={(e) => setCardSelectorSearch(e.target.value)}
              />
            </div>
            
            {/* Add new card button */}
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={() => setShowNewCardDialog(true)}
            >
              <Plus className="h-4 w-4" />
              Add New Card to This Order
            </Button>
            
            {/* Selected count */}
            {selectedCardIds.size > 0 && (
              <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary">
                <span className="font-medium">
                  {selectedCardIds.size} card{selectedCardIds.size !== 1 ? 's' : ''} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCardIds(new Set())}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            )}
            
            {/* Card list - filter out cards already in this order */}
            {filteredAvailableCards.filter(card => {
              // Exclude cards already in this order
              return !editingOrderCards.some(oc => oc.id === card.id);
            }).length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredAvailableCards
                  .filter(card => !editingOrderCards.some(oc => oc.id === card.id))
                  .map((card) => (
                    <div
                      key={card.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedCardIds.has(card.id || '')
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedCardIds);
                        if (newSet.has(card.id || '')) {
                          newSet.delete(card.id || '');
                        } else {
                          newSet.add(card.id || '');
                        }
                        setSelectedCardIds(newSet);
                      }}
                    >
                      <Checkbox
                        checked={selectedCardIds.has(card.id || '')}
                        onCheckedChange={() => {
                          const newSet = new Set(selectedCardIds);
                          if (newSet.has(card.id || '')) {
                            newSet.delete(card.id || '');
                          } else {
                            newSet.add(card.id || '');
                          }
                          setSelectedCardIds(newSet);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium">{card.card}</div>
                        <div className="text-sm text-muted-foreground">
                          {card.sport} • {formatCurrency(typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0'))} • {formatDateUtil(card.purchase_date)}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {cardSelectorSearch ? 'No cards match your search' : 'No available cards in inventory'}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddCardsFromEdit(false);
              setSelectedCardIds(new Set());
              setCardSelectorSearch('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="gradient" 
              onClick={handleAddCardsToOrder}
              disabled={selectedCardIds.size === 0}
            >
              Add Cards ({selectedCardIds.size} {selectedCardIds.size === 1 ? 'card' : 'cards'})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Complete Order Dialog */}
      <Dialog open={completeOrderDialogOpen} onOpenChange={setCompleteOrderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Complete Grading Order</DialogTitle>
            <DialogDescription>
              Set the return date and grades for all cards in this order.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="complete-return-date">Return Date *</Label>
                <Input
                  id="complete-return-date"
                  type="date"
                  value={completeOrderData.returnDate}
                  onChange={(e) => setCompleteOrderData(prev => ({ ...prev, returnDate: e.target.value }))}
                  required
                />
              </div>
              
              {orderCards[completingOrderId || ''] && orderCards[completingOrderId || ''].length > 0 && (
                <div className="space-y-2">
                  <Label>Card Grades</Label>
                  <div className="space-y-2">
                    {completeOrderData.cardGrades.map((cardGrade, index) => {
                      const card = orderCards[completingOrderId || ''][index];
                      const order = gradingOrders.find(o => o.id === completingOrderId);
                      const gradingCompany = order?.company || '';
                      
                      return card ? (
                        <div key={card.id || index} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                          <div className="text-sm">{card.card}</div>
                          <Input
                            type="text"
                            placeholder="Grade (e.g., 10, A, AUTH)"
                            value={cardGrade.grade}
                            onChange={(e) => {
                              const newGrades = [...completeOrderData.cardGrades];
                              newGrades[index].grade = e.target.value;
                              setCompleteOrderData(prev => ({ ...prev, cardGrades: newGrades }));
                            }}
                          />
                          <div className="text-xs text-muted-foreground">
                            {gradingCompany && cardGrade.grade ? `${gradingCompany} ${cardGrade.grade}` : ''}
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleCompleteOrder}>
              Complete Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Order Confirmation Dialog */}
      <Dialog open={deleteOrderDialogOpen} onOpenChange={setDeleteOrderDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Grading Order</DialogTitle>
            <DialogDescription>
              What would you like to do with the cards in this order?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {deletingOrderId && orderCards[deletingOrderId] && (
              <div className="text-sm text-muted-foreground">
                This order contains {orderCards[deletingOrderId].length} card{orderCards[deletingOrderId].length !== 1 ? 's' : ''}.
              </div>
            )}
            
            <div className="space-y-3">
              <div 
                className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  !deleteCardsWithOrder 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:bg-muted/50'
                }`}
                onClick={() => setDeleteCardsWithOrder(false)}
              >
                <Checkbox
                  checked={!deleteCardsWithOrder}
                  onCheckedChange={(checked) => setDeleteCardsWithOrder(!checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1">
                  <div className="font-medium">Send cards back to inventory</div>
                  <div className="text-sm text-muted-foreground">
                    Cards will be removed from this grading order and returned to your inventory. The grading order will be deleted.
                  </div>
                </div>
              </div>
              
              <div 
                className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  deleteCardsWithOrder 
                    ? 'border-destructive bg-destructive/5' 
                    : 'border-border hover:bg-muted/50'
                }`}
                onClick={() => setDeleteCardsWithOrder(true)}
              >
                <Checkbox
                  checked={deleteCardsWithOrder}
                  onCheckedChange={(checked) => setDeleteCardsWithOrder(!!checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1">
                  <div className="font-medium text-destructive">Delete cards along with order</div>
                  <div className="text-sm text-muted-foreground">
                    Cards will be permanently deleted along with the grading order. This action cannot be undone.
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteOrderDialogOpen(false);
              setDeletingOrderId(null);
              setDeleteCardsWithOrder(false);
            }}>
              Cancel
            </Button>
            <Button 
              variant={deleteCardsWithOrder ? "destructive" : "gradient"} 
              onClick={handleDeleteOrder}
            >
              {deleteCardsWithOrder ? 'Delete Order & Cards' : 'Delete Order & Return Cards'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Grading;
